import React, { useEffect, useState, useMemo, memo } from "react";
import { Table, Placeholder, Card, ButtonGroup, Button } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { analyticsActions } from "../../actions/analytics.actions";
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { FaTable, FaChartLine } from "react-icons/fa";
import { getDateRange } from "../../utils/dateUtils";

const TrendsTab = memo(function TrendsTab(props) {
  const dispatch = useDispatch();
  const analyticsReducer = useSelector((state) => state.analytics);
  const [viewMode, setViewMode] = useState("chart"); // "table" or "chart"
  const [chartType, setChartType] = useState("line"); // "line" or "bar"
  const [isStacked, setIsStacked] = useState(true); // whether to stack bars

  useEffect(() => {
    if (!props.user) return;

    // Use clientId prop if provided (for super user viewing specific client),
    // otherwise use user's clientId
    const clientId = props.clientId || props.user.clientId;
    if (!clientId) return;

    const queryParams = {
      clientId: clientId,
      clientGuid: props.user.clientGuid,
    };

    const { startDate, endDate } = getDateRange(props.dateRange);
    if (startDate) queryParams.startDate = startDate;
    if (endDate) queryParams.endDate = endDate;

    dispatch(analyticsActions.getExecutionTrends(queryParams));
    // eslint-disable-next-line
  }, [props.user, props.dateRange, props.clientId]);

  const trends = analyticsReducer.executionTrends || [];
  const loading = analyticsReducer.loadingExecutionTrends;

  // Memoize formatted table data
  const formattedTableData = useMemo(() => {
    return trends.map((trend) => ({
      ...trend,
      formattedSuccessRate: trend.successRate.toFixed(2),
      formattedApiCalls: trend.totalApiCalls.toLocaleString(),
      formattedRecords: trend.totalRecordsProcessed.toLocaleString(),
    }));
  }, [trends]);

  // Memoize the entire chart rendering
  const renderedChart = useMemo(() => {
    const ChartComponent = chartType === "line" ? LineChart : BarChart;
    const DataComponent = chartType === "line" ? Line : Bar;

    return (
      <div>
        <div className="mb-3 d-flex gap-3">
          <ButtonGroup size="sm">
            <Button variant={chartType === "line" ? "primary" : "outline-primary"} onClick={() => setChartType("line")}>
              Line Chart
            </Button>
            <Button variant={chartType === "bar" ? "primary" : "outline-primary"} onClick={() => setChartType("bar")}>
              Bar Chart
            </Button>
          </ButtonGroup>
          {chartType === "bar" && (
            <ButtonGroup size="sm">
              <Button variant={isStacked ? "primary" : "outline-primary"} onClick={() => setIsStacked(true)}>
                Stacked
              </Button>
              <Button variant={!isStacked ? "primary" : "outline-primary"} onClick={() => setIsStacked(false)}>
                Unstacked
              </Button>
            </ButtonGroup>
          )}
        </div>

        <h6 className="mt-4 mb-3">Execution Metrics</h6>
        <ResponsiveContainer width="100%" height={350}>
          <ChartComponent data={trends} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="date" />
            <YAxis />
            <Tooltip />
            <Legend />
            {chartType === "bar" && isStacked ? (
              <>
                <Bar dataKey="successfulExecutions" stackId="a" fill="#198754" name="Successful" />
                <Bar dataKey="failedExecutions" stackId="a" fill="#dc3545" name="Failed" />
              </>
            ) : (
              <>
                <DataComponent type="monotone" dataKey="totalExecutions" stroke="#0d6efd" fill="#0d6efd" name="Total Executions" />
                <DataComponent type="monotone" dataKey="successfulExecutions" stroke="#198754" fill="#198754" name="Successful" />
                <DataComponent type="monotone" dataKey="failedExecutions" stroke="#dc3545" fill="#dc3545" name="Failed" />
              </>
            )}
          </ChartComponent>
        </ResponsiveContainer>

        <h6 className="mt-5 mb-3">API Calls</h6>
        <ResponsiveContainer width="100%" height={350}>
          <ChartComponent data={trends} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="date" />
            <YAxis />
            <Tooltip />
            <Legend />
            <DataComponent type="monotone" dataKey="totalApiCalls" stroke="#6610f2" fill="#6610f2" name="Total API Calls" />
          </ChartComponent>
        </ResponsiveContainer>

        <h6 className="mt-5 mb-3">Records Processed</h6>
        <ResponsiveContainer width="100%" height={350}>
          <ChartComponent data={trends} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="date" />
            <YAxis />
            <Tooltip />
            <Legend />
            <DataComponent type="monotone" dataKey="totalRecordsProcessed" stroke="#fd7e14" fill="#fd7e14" name="Records Processed" />
          </ChartComponent>
        </ResponsiveContainer>
      </div>
    );
  }, [trends, chartType, isStacked]);

  return (
    <div>
      <Card>
        <Card.Header>
          <div className="d-flex justify-content-between align-items-center">
            <h5 className="mb-0">Execution Trends Over Time</h5>
            <ButtonGroup>
              <Button
                variant={viewMode === "chart" ? "primary" : "outline-primary"}
                size="sm"
                onClick={() => setViewMode("chart")}
              >
                <FaChartLine className="me-2" />
                Chart View
              </Button>
              <Button
                variant={viewMode === "table" ? "primary" : "outline-primary"}
                size="sm"
                onClick={() => setViewMode("table")}
              >
                <FaTable className="me-2" />
                Table View
              </Button>
            </ButtonGroup>
          </div>
        </Card.Header>
        <Card.Body>
          {loading && (
            <Placeholder as={"div"} animation="glow">
              <Placeholder xs={12} size="lg" style={{ height: "300px" }} />
            </Placeholder>
          )}

          {!loading && trends.length > 0 && viewMode === "chart" && renderedChart}

          {!loading && trends.length > 0 && viewMode === "table" && (
            <Table striped bordered hover responsive>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Total Executions</th>
                  <th>Successful</th>
                  <th>Failed</th>
                  <th>Success Rate</th>
                  <th>Total API Calls</th>
                  <th>Records Processed</th>
                </tr>
              </thead>
              <tbody>
                {formattedTableData.map((trend) => (
                  <tr key={trend.date}>
                    <td><strong>{trend.date}</strong></td>
                    <td>{trend.totalExecutions}</td>
                    <td>{trend.successfulExecutions}</td>
                    <td>{trend.failedExecutions}</td>
                    <td>{trend.formattedSuccessRate}%</td>
                    <td>{trend.formattedApiCalls}</td>
                    <td>{trend.formattedRecords}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}

          {!loading && trends.length === 0 && (
            <div className="text-center text-muted py-5">
              <p>No trend data available for the selected date range</p>
            </div>
          )}
        </Card.Body>
      </Card>
    </div>
  );
});

export default TrendsTab;
